import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgForm, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IonicModule } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { SavePassword } from 'capacitor-ios-autofill-save-password';
import { AuthService } from '../../services/auth/auth.service';
import { forgotPasswordQueryParams } from '../../services/auth/login-error-key';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { lastValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BiometryType } from '@capgo/capacitor-native-biometric';
import { BiometricService } from '../../services/biometric/biometric.service';
import { QrScannerService } from 'src/app/core/services/qr-scanner/qr-scanner.service';
import { TerminalService } from 'src/app/modules/terminal/services/terminal.service';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { AutofillDirective } from 'src/app/core/services/autofill.directive';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CompanySelectionDialogComponent } from '../../components/company-selection-dialog/company-selection-dialog.component';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { AuthLegalFooterComponent } from '../../components/auth-legal-footer/auth-legal-footer.component';
import { XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent, XdsFormFieldComponent, XdsInputComponent, XdsCheckboxComponent } from '../../../../shared/ui';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    imports: [CommonModule, FormsModule, RouterModule, TranslateModule, IonicModule, AutofillDirective, MatDialogModule, AuthLegalFooterComponent, XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent, XdsFormFieldComponent, XdsInputComponent, XdsCheckboxComponent]
})
export class LoginComponent implements OnInit, OnDestroy {
  isTerminalMode = false;
  /**
   * C-08: Ist die Sitzung von selbst ausgelaufen? Gesetzt aus `?reason=expired`, das
   * `AuthService.goToLogin('expired')` anhängt. Nur dann erscheint der Hinweis — beim
   * bewussten Abmelden und beim ersten Besuch wäre er falsch.
   */
  sessionExpired = false;
  /** FE-12 P2: letzte Anmeldung scheiterte an einer Kontosperre (Sperrtext + Reset-Knopf). */
  lockedOut = false;
  isLoading = false;

  private companySelectionDialogRef?: MatDialogRef<CompanySelectionDialogComponent, number | undefined>;

  formModel = {
    EmailOrUserName: '',
    Password: '',
    RememberMe: false,
  };

  // Pairing form model (only used in Terminal mode)
  pairingFormModel = {
    code: '',
  };

  // QR Scanner
  isScanning = false;
  isScannerSupported = false;

  errorStatus: any;
  showLocalEmailHint = false;
  isMobile: boolean = false;
  private destroy$ = new Subject<void>();

  biometricAvailable = false;
  biometricEnabled = false;
  biometricType: BiometryType | undefined;
  biometricTypeName = '';
  showBiometricButton = false;
  enableBiometric = false;
  private skipAutoBiometric = false;
  showPassword = false;
  isIos = false;
  formSubmitted = false;

  constructor(
    private route: ActivatedRoute,
    private service: AuthService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private biometricService: BiometricService,
    private qrScannerService: QrScannerService,
    private terminalService: TerminalService,
    private platformService: PlatformService,
    public themeService: ThemeService,
    private dialog: MatDialog
  ) {
    this.isIos = this.platformService.isIos();
    if (
      this.route.snapshot.queryParams &&
      this.route.snapshot.queryParams.param
    ) {
      localStorage.setItem('token', this.route.snapshot.queryParams.param);
    }
    this.errorStatus = this.service.error;

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });
  }

  async ngOnInit(): Promise<void> {
    await this.initializeComponent();
  }

  // Ionic Lifecycle Hook - wird bei jeder Navigation zur Seite aufgerufen
  async ionViewWillEnter(): Promise<void> {
    await this.initializeComponent();
  }

  private async initializeComponent(): Promise<void> {
    // C-08: In `initializeComponent` und nicht nur im Konstruktor — die Ionic-Route
    // ruft `ionViewWillEnter` bei jeder Navigation auf, und genau darüber kommt man nach
    // einem Ablauf hierher, ohne dass die Komponente neu gebaut wird.
    this.sessionExpired =
      this.route.snapshot.queryParamMap.get('reason') === 'expired';

    const result = this.breakpointObserver.isMatched([
      Breakpoints.XSmall,
      Breakpoints.Small,
    ]);
    this.isMobile = result;

    // Check if QR scanner is supported
    this.isScannerSupported = await this.qrScannerService.isScanningSupported();

    // Auto-detect Terminal-Modus: wenn terminal_guid vorhanden, war dieses Geraet als Terminal gepairt
    if (!this.isTerminalMode && localStorage.getItem('terminal_guid')) {
      this.isTerminalMode = true;
    }

    // WICHTIG: Prüfe ob Terminal bereits gekoppelt ist (Token vorhanden)
    // Dies ermöglicht Re-Login ohne erneutes Pairing
    if (this.isTerminalMode) {
      const terminalToken = localStorage.getItem('token');
      const terminalGuid = localStorage.getItem('terminal_guid');

      if (terminalToken && terminalGuid) {
        // Prüfe Token-Gültigkeit
        try {
          const payload = JSON.parse(window.atob(terminalToken.split('.')[1]));
          const exp = payload.exp;
          const now = Math.floor(Date.now() / 1000);

          if (exp && exp > now) {
            // Token ist noch gültig → Direkt zu Terminal-Dashboard
            await this.router.navigate(['/terminal-dashboard']);
            return;
          } else {
            // Token abgelaufen → Versuche RefreshToken-Erneuerung
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              try {
                await lastValueFrom(this.service.tryRefreshingTokens(terminalToken));
                // Refresh erfolgreich → Direkt zu Terminal-Dashboard
                await this.router.navigate(['/terminal-dashboard']);
                return;
              } catch (refreshErr) {
                console.warn('[Terminal-Login] Token refresh fehlgeschlagen, neues Pairing noetig:', refreshErr);
              }
            }
            // Refresh fehlgeschlagen oder kein RefreshToken → Pairing-Code neu eingeben
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('terminal_guid');
            localStorage.removeItem('terminal_name');
          }
        } catch (e) {
          // Token konnte nicht geparst werden → Lösche korrupte Daten
          console.error('[Terminal-Login] Token parsing fehlgeschlagen:', e);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('terminal_guid');
          localStorage.removeItem('terminal_name');
        }
      }
    }

    const savedRememberMe = localStorage.getItem('rememberMe');
    if (savedRememberMe === 'true') {
      this.formModel.RememberMe = true;
      const savedEmail = localStorage.getItem('rememberedEmail');
      const savedTerminal = localStorage.getItem('rememberedTerminal');

      if (savedEmail && !this.isTerminalMode) {
        this.formModel.EmailOrUserName = savedEmail;
      } else if (savedTerminal && this.isTerminalMode) {
        this.formModel.EmailOrUserName = savedTerminal;
      }
    }

    // Subscribe to AuthService loading state for login spinner
    this.service.isLoading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
    });

    // FE-12 P2: Kontosperre inline mit Weg zum Passwort-Reset statt Toast.
    this.service.loginLockedOut$.pipe(takeUntil(this.destroy$)).subscribe((gesperrt) => {
      this.lockedOut = gesperrt;
    });

    // Multi-Company: Subscribe to company selection requirement
    this.service.requiresCompanySelection$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      if (state.show && !this.companySelectionDialogRef) {
        this.companySelectionDialogRef = this.dialog.open(CompanySelectionDialogComponent, {
          width: '480px',
          maxWidth: '95vw',
          data: { companies: state.companies },
        });
        this.companySelectionDialogRef.afterClosed().subscribe((companyId) => {
          this.companySelectionDialogRef = undefined;
          // setTimeout: afterClosed() kann noch innerhalb des schließenden Change-Detection-Zyklus
          // emittieren; ohne Verzögerung löst das Aktualisieren von isLoading via AuthService
          // ein NG0100 (ExpressionChangedAfterItHasBeenCheckedError) in LoginComponent aus.
          setTimeout(() => {
            if (companyId) {
              this.onCompanySelected(companyId);
            } else {
              this.onCompanySelectionCancelled();
            }
          });
        });
      }
    });

    if (localStorage.getItem('token') != null && !this.isTerminalMode) {
      await this.navigateBasedOnRole();
      return; // Bereits eingeloggt, nicht weitermachen
    } else if (this.service.Email != null) {
      this.formModel.EmailOrUserName = this.service.Email;
      this.formModel.Password = '';
    }

    // Biometrie initialisieren und DANN automatisch Fingerprint zeigen (wie Banking-Apps)
    if (await this.shouldCheckBiometric()) {
      await this.initializeBiometric();

      // Automatisch Fingerprint-Prompt zeigen wenn Biometrie aktiviert ist
      // (wie bei Banking-Apps: App öffnen → sofort Fingerprint)
      if (
        !localStorage.getItem('token') &&
        !this.skipAutoBiometric &&
        this.biometricEnabled &&
        this.showBiometricButton
      ) {
        // Kurze Verzögerung damit UI aufgebaut ist, dann Fingerprint
        setTimeout(() => {
          this.loginWithBiometric(true);
        }, 150);
      }
    }
  }

  private async shouldCheckBiometric(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }
    // WICHTIG: Immer true zurückgeben auf nativen Plattformen!
    // initializeBiometric() prüft dann ob Biometrie verfügbar ist und zeigt
    // entweder die "Aktivieren"-Checkbox oder den "Login mit Biometrie"-Button.
    // Der alte Code gab hier false zurück wenn Biometrie noch nicht aktiviert war,
    // dadurch wurde die Checkbox zum Aktivieren nie angezeigt!
    return true;
  }

  private async initializeBiometric(): Promise<void> {
    this.biometricAvailable =
      await this.biometricService.isBiometricAvailable();

    if (this.biometricAvailable) {
      this.biometricType = await this.biometricService.getBiometryType();
      this.biometricTypeName = this.biometricService.getBiometryTypeName(
        this.biometricType
      );

      this.biometricEnabled = await this.biometricService.isBiometricEnabled();

      if (this.biometricEnabled) {
        const credentialsForDisplay =
          await this.biometricService.getCredentials();
        this.showBiometricButton = credentialsForDisplay !== null;

        if (credentialsForDisplay && this.showBiometricButton) {
          this.formModel.EmailOrUserName =
            credentialsForDisplay.emailOrUsername;
          this.isTerminalMode = credentialsForDisplay.isTerminalMode;
          this.formModel.Password = '';

          // ÜBERARBEITET: Remember Me Status aus Biometric Credentials
          this.formModel.RememberMe = credentialsForDisplay.rememberMe || false;
        }
      }
    }
  }

  // ÜBERARBEITET: Biometric Login mit RefreshToken Unterstützung
  async loginWithBiometric(isAutomatic: boolean = false): Promise<void> {
    if (!this.biometricAvailable || !this.biometricEnabled) {
      if (!isAutomatic) {
        this.errorStatus = {
          showError: true,
          errorMessage: 'Biometrie ist nicht verfügbar',
        };
      }
      return;
    }

    this.isLoading = true;

    try {
      const credentials =
        await this.biometricService.getCredentialsWithBiometric();

      if (credentials) {
        this.formModel.EmailOrUserName = credentials.emailOrUsername;
        this.formModel.Password = credentials.password;
        this.isTerminalMode = credentials.isTerminalMode;
        this.formModel.RememberMe = credentials.rememberMe || true;

        // Login mit RememberMe Status (only Employee login)
        if (this.isTerminalMode) {
          // Terminal login via biometric not supported - terminals use pairing
          this.errorStatus = {
            showError: true,
            errorMessage: 'Terminal-Login nur per Pairing-Code möglich',
          };
          this.isLoading = false;
          return;
        } else {
          this.service.login({
            Email: this.formModel.EmailOrUserName,
            Password: this.formModel.Password,
            RememberMe: this.formModel.RememberMe,
          });
        }
      } else {
        if (isAutomatic) {
          this.skipAutoBiometric = true;
          this.showBiometricButton = false;
        }
        this.errorStatus = {
          showError: false,
          errorMessage: '',
        };
      }
    } catch (error) {
      console.error('Biometric login failed:', error);

      if (isAutomatic) {
        this.skipAutoBiometric = true;
        this.showBiometricButton = false;
      } else {
        this.errorStatus = {
          showError: true,
          errorMessage:
            'Biometrische Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
        };
      }
    } finally {
      this.isLoading = false;
    }
  }

  async onSubmit(form: NgForm): Promise<void> {
    this.formSubmitted = true;

    if (form.invalid) {
      Object.values(form.controls).forEach(control => control.markAsTouched());
      return;
    }

    this.formModel.EmailOrUserName =
      this.formModel.EmailOrUserName.trim().replace(/\s+/g, '');

    this.checkLocalEmailHint();

    if (this.formModel.RememberMe) {
      localStorage.setItem('rememberMe', 'true');

      if (this.isTerminalMode) {
        localStorage.setItem(
          'rememberedTerminal',
          this.formModel.EmailOrUserName
        );
      } else {
        localStorage.setItem('rememberedEmail', this.formModel.EmailOrUserName);
      }
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedTerminal');
    }

    // Terminal mode should not reach here - only pairing is allowed
    if (this.isTerminalMode) {
      this.errorStatus = {
        showError: true,
        errorMessage: 'Terminal-Login nur per Pairing-Code möglich. Bitte nutzen Sie den Button unten.',
      };
      return;
    }

    // ÜBERARBEITET: Biometric mit RememberMe Status speichern (only for employees)
    if (this.enableBiometric && this.biometricAvailable) {
      await this.biometricService.saveCredentialsWithRememberMe(
        this.formModel.EmailOrUserName,
        this.formModel.Password,
        this.isTerminalMode,
        this.formModel.RememberMe
      );
      this.biometricEnabled = true;
      this.showBiometricButton = true;
    }

    // Employee login - Spinner wird vom AuthService gesteuert
    this.service.login({
      Email: this.formModel.EmailOrUserName,
      Password: this.formModel.Password,
      RememberMe: this.formModel.RememberMe,
    });

    if (Capacitor.getPlatform() === 'ios') {
      await SavePassword.promptDialog({
        username: this.formModel.EmailOrUserName,
        password: this.formModel.Password,
      });
    }
  }

  async disableBiometric(): Promise<void> {
    await this.biometricService.clearCredentials();
    this.biometricEnabled = false;
    this.showBiometricButton = false;
    this.enableBiometric = false;
    this.skipAutoBiometric = true;

    this.formModel.EmailOrUserName = '';
    this.formModel.Password = '';
  }

  private async navigateBasedOnRole(): Promise<void> {
    // C-08: Zurück zu der Seite, an der die Sitzung ablief — vor allen Rollen-Vorgaben.
    //
    // Diese Methode greift nur im Sonderfall „schon angemeldet, landet trotzdem auf
    // /login". Der ECHTE Anmelde-Weg navigiert über
    // `AuthService.getRedirectUrlAfterLogin()`; dort sitzt derselbe Rückweg. Die Prüfung
    // des Parameters kommt bewusst aus dem Service — eine zweite Kopie hier wäre die Sorte
    // Doppelung, die beim ersten Nachschärfen auseinanderläuft.
    const zurueck = this.service.rueckwegAusQueryParam();
    if (zurueck && !this.service.isTerminal) {
      this.router.navigateByUrl(zurueck);
      return;
    }

    if (this.service.isTerminal) {
      this.router.navigateByUrl('/terminal-dashboard');
      return;
    }

    if (this.service.isManegeOrTeamLeader) {
      // Phase 12 Cleanup: Die fruehere Weiterleitung zum `/registered-stepper`
      // bei unerledigtem Welcome-Step wurde entfernt. Die neue Plattform zeigt
      // Welcome-Hinweise via `OnboardingWelcomeCardComponent` direkt im
      // Dashboard — das respektiert den Kill-Switch und die Tour-State-
      // Machine, ohne dass die Login-Route eine separate Onboarding-Seite
      // erzwingt.
      this.router.navigateByUrl('/dashboard');
      return;
    }

    if (this.isMobile) {
      this.router.navigateByUrl('/employee-mobile');
    } else {
      this.router.navigateByUrl('/employeedashboard');
    }
  }

  loginExternal(): void {
    this.service.loginExternal();
  }

  toggleLoginMode(): void {
    this.isTerminalMode = !this.isTerminalMode;
    this.formModel.EmailOrUserName = '';
    this.formModel.Password = '';
    this.formModel.RememberMe = false;
    this.pairingFormModel.code = ''; // Reset pairing code
    this.errorStatus = { showError: false, errorMessage: '' };
    this.showLocalEmailHint = false;
    this.enableBiometric = false;
    this.formSubmitted = false;
    this.skipAutoBiometric = true;
    this.showBiometricButton = false;

    // Load saved credentials for employee mode only
    if (!this.isTerminalMode && localStorage.getItem('rememberMe') === 'true') {
      const savedEmail = localStorage.getItem('rememberedEmail');
      if (savedEmail) {
        this.formModel.EmailOrUserName = savedEmail;
        this.formModel.RememberMe = true;
      }
    }
  }

  private checkLocalEmailHint(): void {
    if (
      !this.isTerminalMode &&
      this.formModel.EmailOrUserName.toLowerCase().endsWith('.local')
    ) {
      this.showLocalEmailHint = true;
    } else {
      this.showLocalEmailHint = false;
    }
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  // Submit pairing code (Terminal mode only)
  async submitPairingCode(): Promise<void> {
    const code = this.pairingFormModel.code.trim().toUpperCase();

    if (code.length !== 8) {
      this.errorStatus = {
        showError: true,
        errorMessage: 'Pairing-Code muss genau 8 Zeichen lang sein'
      };
      return;
    }

    this.isLoading = true;
    this.errorStatus = { showError: false, errorMessage: '' };

    try {
      // Pairing direkt durchführen (nicht zur Pairing-Seite navigieren!)
      const response = await lastValueFrom(this.terminalService.pairTerminal(code));

      // Token speichern
      if (response.Token) {
        localStorage.setItem('token', response.Token);
        localStorage.setItem('terminal_guid', response.TerminalGuid || '');
        localStorage.setItem('terminal_name', response.Name || '');

        // RefreshToken speichern fuer automatische Token-Erneuerung
        if (response.RefreshToken) {
          localStorage.setItem('refreshToken', response.RefreshToken);
        }

        // AuthService-Flags aktualisieren (analog zu terminal-pairing.component.ts)
        // Ohne dieses Update bleiben die Flags auf den Default-Werten und
        // determineTargetUrlBasedOnRoleAndDevice() leitet bei Breakpoint-Wechsel
        // fälschlicherweise zur Employee-Ansicht um.
        this.service.isTerminal = true;
        this.service.isManegeOrTeamLeader = false;
        this.service.setMyCurrentRole();

        // Altes Session-Expired Flag loeschen (neues Pairing = neue Session)
        localStorage.removeItem('terminalSessionExpired');
      }

      this.isLoading = false;

      // Navigate to terminal dashboard
      await this.router.navigate(['/terminal-dashboard']);
    } catch (error) {
      console.error('Terminal pairing failed:', error);
      this.errorStatus = {
        showError: true,
        errorMessage: 'Pairing fehlgeschlagen. Bitte überprüfen Sie den Code und versuchen Sie es erneut.'
      };
      this.isLoading = false;
    }
  }

  // Scan QR code (Terminal mode only)
  async scanQrCode(): Promise<void> {
    if (this.isScanning) {
      return;
    }

    this.isScanning = true;
    this.errorStatus = { showError: false, errorMessage: '' };

    try {
      const scannedCode = await this.qrScannerService.scanQrCode();

      if (scannedCode) {
        this.pairingFormModel.code = scannedCode;

        // Auto-submit if valid code
        if (scannedCode.length === 8) {
          await this.submitPairingCode();
        }
      }
    } catch (error) {
      console.error('[Login] QR scan error:', error);
      this.errorStatus = {
        showError: true,
        errorMessage: 'QR-Code konnte nicht gescannt werden. Bitte versuchen Sie es erneut oder geben Sie den Code manuell ein.'
      };
    } finally {
      this.isScanning = false;
    }
  }

  // NEW: Format pairing code input
  onPairingCodeInput(event: any): void {
    let value = event.target.value.toUpperCase();
    // Remove any non-alphanumeric characters
    value = value.replace(/[^A-Z0-9]/g, '');
    // Limit to 8 characters
    value = value.substring(0, 8);
    this.pairingFormModel.code = value;
  }

  // ─────────── Multi-Company Methods ───────────

  /**
   * Called when user selects a company in the dialog
   */
  onCompanySelected(companyId: number): void {
    this.service.loginWithSelectedCompany(companyId);
  }

  /**
   * Called when user cancels the company selection dialog
   */
  onCompanySelectionCancelled(): void {
    this.service.cancelCompanySelection();
  }

  /** Vorbelegung für `/forgotpassword`: die eingegebene Adresse (Router kodiert sie). */
  get lockedOutResetQueryParams(): { email?: string } {
    return forgotPasswordQueryParams(this.formModel.EmailOrUserName);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }
}
